import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import { subscribeActivity } from "@/lib/activityBus";

// Messages de "consultation / exploration" — majoritaires
const ACTIONS_CONSULT = {
  fr: ["a ouvert une conversation infinie", "consulte une capsule de curiosité", "écoute Radio Cocoon", "a noté une épiphanie", "explore l'Arène des idées", "parcourt Le Terrier du Lapin", "creuse une question de La Boussole", "vote dans l'Arène des idées", "découvre un nouveau concept"],
  en: ["opened an infinite conversation", "is browsing a curiosity capsule", "is listening to Radio Cocoon", "noted an epiphany", "is exploring the Arena of ideas", "is wandering The Rabbit Hole", "is digging into a Compass question", "is voting in the Arena of ideas", "is discovering a new concept"],
  es: ["abrió una conversación infinita", "consulta una cápsula de curiosidad", "escucha Radio Cocoon", "anotó una epifanía", "explora la Arena de las ideas", "recorre La Madriguera", "profundiza una pregunta de La Brújula", "vota en la Arena de las ideas", "descubre un nuevo concepto"],
  nl: ["opende een oneindig gesprek", "bekijkt een nieuwsgierigheidscapsule", "luistert naar Radio Cocoon", "noteerde een epifanie", "verkent de Arena van ideeën", "dwaalt door Het Konijnenhol", "diept een Kompas-vraag uit", "stemt in de Arena van ideeën", "ontdekt een nieuw concept"],
};

// Messages d'"inscription / adhésion" — rares
const ACTIONS_SIGNUP = {
  fr: ["vient de s'inscrire", "vient de rejoindre Le Cocoon"],
  en: ["just signed up", "just joined The Cocoon"],
  es: ["acaba de registrarse", "acaba de unirse a El Cocoon"],
  nl: ["heeft zich zojuist ingeschreven", "is zojuist lid geworden van The Cocoon"],
};

const AGO = {
  fr: ["à l'instant", "il y a 1 min", "il y a 2 min", "il y a 3 min", "il y a 5 min", "il y a 8 min", "il y a 12 min"],
  en: ["just now", "1 min ago", "2 min ago", "3 min ago", "5 min ago", "8 min ago", "12 min ago"],
  es: ["ahora mismo", "hace 1 min", "hace 2 min", "hace 3 min", "hace 5 min", "hace 8 min", "hace 12 min"],
  nl: ["zojuist", "1 min geleden", "2 min geleden", "3 min geleden", "5 min geleden", "8 min geleden", "12 min geleden"],
};
const PREP = { fr: "de", en: "from", es: "de", nl: "uit" };

function rand(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

// Temps pondérés vers le récent (index bas = plus fréquent)
function randAgo(list) {
  const w = Math.floor(Math.pow(Math.random(), 1.9) * list.length);
  return list[Math.min(w, list.length - 1)];
}

export function SocialProof() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [ev, setEv] = useState(null);
  const [closed, setClosed] = useState(false);
  const hideRef = useRef(null);

  useEffect(() => {
    if (closed) return;
    const unsub = subscribeActivity((a) => {
      const prep = PREP[lang] || PREP.fr;
      const pool = a.useSignup ? (ACTIONS_SIGNUP[lang] || ACTIONS_SIGNUP.fr) : (ACTIONS_CONSULT[lang] || ACTIONS_CONSULT.fr);
      setEv({
        key: a.id,
        flag: a.flag,
        initial: a.initial,
        headline: `${a.name} ${prep} ${a.city}`,
        action: rand(pool),
        ago: randAgo(AGO[lang] || AGO.fr),
      });
      clearTimeout(hideRef.current);
      hideRef.current = setTimeout(() => setEv(null), 5200);
    });
    return () => { unsub(); clearTimeout(hideRef.current); };
  }, [lang, closed]);

  if (closed) return null;

  return (
    <div className="fixed left-4 bottom-4 z-40 pointer-events-none" data-testid="social-proof">
      <AnimatePresence>
        {ev && (
          <motion.div
            key={ev.key}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="pointer-events-auto relative flex items-center gap-3 glass rounded-2xl border border-primary/25 pl-3 pr-9 py-2.5 max-w-[19rem] shadow-2xl"
            data-testid="social-proof-toast"
          >
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/35 to-primary/10 border border-primary/40 flex items-center justify-center">
                <span className="font-display text-primary text-lg leading-none">{ev.initial}</span>
              </div>
              <span className="absolute -bottom-1 -right-1 text-sm leading-none" aria-hidden>{ev.flag}</span>
            </div>
            <div className="min-w-0">
              <p className="text-sm text-foreground/90 leading-snug truncate">
                <span className="font-semibold text-primary">{ev.headline}</span>
              </p>
              <p className="text-xs text-foreground/60 leading-snug">{ev.action}</p>
              <p className="text-[10px] uppercase tracking-wider text-primary/50 mt-0.5 flex items-center gap-1">
                <Sparkles size={10} className="text-primary/60" /> {ev.ago}
              </p>
            </div>
            <button
              onClick={() => setClosed(true)}
              data-testid="social-proof-close"
              className="absolute top-2 right-2 text-foreground/35 hover:text-foreground/70 transition-colors"
              aria-label="close"
            >
              <X size={13} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
